/**
 * One row per Worker, whatever the database view returned (#879).
 *
 * `brunel.worker_terminals` is meant to be one row per Worker, but a Worker can hold more
 * than one unclosed terminal and more than one unretired workforce session (one per
 * project), and the older hosted view joins all of them. Every screen reads these rows, so
 * a joined row per project turned into a second Worker card, a doubled machine count and an
 * ambiguous Terminal target.
 *
 * This boundary collapses the rows by canonical `worker_id` before anything reads them. The
 * terminal and the workforce session are chosen separately, because they are independent
 * joins: the terminal is the open, healthy, most recently active one; the session is the one
 * that belongs to the running task's project, else the one that is working, else an
 * active/restored one. When a task is running, its own project is the project shown. Nothing
 * is closed, retired or rewritten here - this only decides which recorded facts to show.
 */
import type { WorkerTerminalRow } from './data';

/** The running task's own project, keyed by task id, from `brunel.running_tasks`. */
export interface RunningTaskProject {
  readonly task_id: string;
  readonly project_id: string;
  readonly project_name: string | null;
}

const LIVE_LIFECYCLES = new Set(['active', 'restored']);
const TERMINAL_STATES_THAT_NEED_THE_OWNER = new Set(['waiting_for_approval', 'waiting_for_input']);

function millis(at: string | null): number {
  const parsed = at === null ? Number.NaN : Date.parse(at);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compareIds(left: string | null, right: string | null): number {
  // Plain code-unit order, the same order the database view breaks its ties in.
  const a = left ?? '￿';
  const b = right ?? '￿';
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Higher is more authoritative. Compared field by field, so no weight can outvote another. */
function terminalRank(row: WorkerTerminalRow): readonly number[] {
  return [
    row.session_id !== null && row.closed_at === null ? 1 : 0,
    row.failure === null ? 1 : 0,
    row.terminal_status !== null && TERMINAL_STATES_THAT_NEED_THE_OWNER.has(row.terminal_status) ? 1 : 0,
    millis(row.last_activity_at ?? row.opened_at),
  ];
}

function sessionRank(row: WorkerTerminalRow, taskProjectId: string | null): readonly number[] {
  return [
    taskProjectId !== null && row.project_id === taskProjectId ? 1 : 0,
    row.session_status === 'working' ? 1 : 0,
    row.lifecycle !== null && LIVE_LIFECYCLES.has(row.lifecycle) ? 1 : 0,
    row.workforce_session_id !== null ? 1 : 0,
    row.session_status !== null && row.session_status !== 'offline' ? 1 : 0,
  ];
}

function compareRank(left: readonly number[], right: readonly number[]): number {
  for (let index = 0; index < left.length; index += 1) {
    const difference = (right[index] ?? 0) - (left[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

function best<T>(items: readonly T[], compare: (left: T, right: T) => number): T {
  return [...items].sort(compare)[0] as T;
}

/**
 * Collapse joined rows to exactly one per `worker_id`, in Worker number order. A Worker with a
 * single row comes back unchanged.
 */
export function canonicalWorkerTerminalRows(
  rows: readonly WorkerTerminalRow[],
  running: readonly RunningTaskProject[] = [],
): readonly WorkerTerminalRow[] {
  const taskProject = new Map(running.map((task) => [task.task_id, task]));
  const groups = new Map<string, WorkerTerminalRow[]>();
  for (const row of rows) {
    const group = groups.get(row.worker_id);
    if (group === undefined) groups.set(row.worker_id, [row]);
    else group.push(row);
  }

  const result: WorkerTerminalRow[] = [];
  for (const group of groups.values()) {
    const withTask = group.find((row) => row.task_id !== null) ?? null;
    const task = withTask?.task_id ? taskProject.get(withTask.task_id) ?? null : null;
    const project = task?.project_id ?? null;

    if (group.length === 1) {
      const [only] = group as [WorkerTerminalRow];
      result.push(task === null ? only : { ...only, project_name: task.project_name ?? only.project_name });
      continue;
    }

    const terminal = best(group, (left, right) =>
      compareRank(terminalRank(left), terminalRank(right)) || compareIds(left.session_id, right.session_id));
    const session = best(group, (left, right) =>
      compareRank(sessionRank(left, project), sessionRank(right, project))
        || compareIds(left.workforce_session_id, right.workforce_session_id));
    const work = withTask ?? session;

    result.push({
      // The Worker itself: identical on every joined row.
      ...session,
      // The terminal that belongs to the current work.
      session_id: terminal.session_id, terminal_status: terminal.terminal_status, pid: terminal.pid,
      shell: terminal.shell, cwd: terminal.cwd, opened_at: terminal.opened_at,
      terminal_origin: terminal.terminal_origin,
      last_activity_at: terminal.last_activity_at, closed_at: terminal.closed_at,
      exit_code: terminal.exit_code, failure: terminal.failure,
      input_holder: terminal.input_holder, input_held: terminal.input_held,
      input_holder_controller: terminal.input_holder_controller,
      input_holder_controller_name: terminal.input_holder_controller_name,
      node_id: terminal.node_id, node_name: terminal.node_name,
      // The workforce session, with the running task's own project when a task is running.
      workforce_session_id: session.workforce_session_id, offering_key: session.offering_key,
      project_id: session.project_id, lifecycle: session.lifecycle, session_status: session.session_status,
      project_name: task?.project_name ?? session.project_name,
      // The current task.
      task_id: work.task_id, task_title: work.task_title,
      task_started_at: work.task_started_at, task_deadline_at: work.task_deadline_at,
    });
  }
  return result.sort((left, right) => left.worker_number - right.worker_number);
}
